import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { personalDataApi } from 'sections/LKSection/shared/api'
import { PersonalData } from 'sections/LKSection/shared/components/PersonalData/PersonalData'
import { RatingBadge } from 'sections/LKSection/shared/components/RatingBadge/RatingBadge'
import { HeaderButton } from 'sections/LKSection/shared/components/UI/HeaderButton/HeaderButton'
import { ItemBox } from 'sections/LKSection/shared/components/UI/ItemBox/ItemBox'
import { PersonalUserDataDTO } from 'sections/LKSection/shared/types/types'
import { Flex, Text } from 'components/uiKit'
import { getEditEmployeePageRoute } from 'utils/helpers/routes/getRoutes'
import { useResize } from 'utils/hooks/useResize'
import { phonePipe } from 'utils/pipes'
import { Country } from 'shared/types/types'

import { Employee } from '../../stores/entity/Employee'
import { DeleteEmployeeModal } from './Modals/DeleteEmployeeModal'
import { EditEmployeeSnilsModal } from './Modals/EditEmployeeSnilsModal'

import deleteIcon from 'assets/img/lk-section/Employee/Delete.svg'
import edit from 'assets/img/lk-section/Employee/Edit.svg'
import setting from 'assets/img/lk-section/Employee/Setting.svg'

type EmployeeItemHeaderProps = {
  employee: Employee
}

export const EmployeeItemHeader = observer(({ employee }: EmployeeItemHeaderProps) => {
  const { viewPvzUiStore } = useStore()

  const { isTablet } = useResize()
  const navigate = useNavigate()
  const { pvzId } = useParams()

  const [personalData, setPersonalData] = useState<PersonalUserDataDTO | null>(null)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const inactive = employee.isDeleted
  const selectedPvzId = viewPvzUiStore.selectedPvz?.id
  const isOwner = selectedPvzId ? viewPvzUiStore.isOwner(selectedPvzId) : false
  const isCurrentUserOwner = isOwner && employee.id === viewPvzUiStore.appStore.user.info.user_id

  const deleteButtonHandler = async () => {
    setIsDeleteModalVisible(true)

    if (!personalData) {
      const personalData = await personalDataApi.getUserData(employee.id ?? 0)
      setPersonalData(personalData)
    }
  }

  const settingButtonHandler = () => {
    if (pvzId && employee.id) {
      navigate(getEditEmployeePageRoute(+pvzId, employee.id))
    }
  }

  return (
    <ItemBox withoutClick>
      <Flex
        align={isTablet ? 'flex-start' : 'center'}
        justify="space-between"
        gap={isTablet ? 16 : 40}
        vertical={isTablet}
        style={{ width: '100%' }}
      >
        <Flex vertical>
          <PersonalData<PersonalUserDataDTO>
            id={employee.id ?? 0}
            getPersonalData={personalDataApi.getUserData}
            setPersonalData={setPersonalData}
            data={personalData}
          >
            <Flex vertical>
              <Text type={employee.isDeleted ? 'secondary' : undefined}>
                {`${personalData?.firstName} ${personalData?.lastName}`}
              </Text>

              <Text type="secondary">
                {phonePipe(String(personalData?.phone), employee.country) ?? personalData?.phone}
              </Text>
            </Flex>
          </PersonalData>

          {!personalData && <Text>{phonePipe(String(employee.phone), employee.country, true)}</Text>}
        </Flex>

        <Flex align="center" gap={32} justify="space-between" style={{ width: isTablet ? '100%' : undefined }}>
          <Flex style={{ minWidth: isTablet ? undefined : 100 }} vertical>
            <Text>
              ID: <Text copyable>{employee.id}</Text>
            </Text>
            <RatingBadge rate={employee.rating} type="img" />
          </Flex>

          <Flex gap={8} style={{ minWidth: inactive ? 100 : 130 }} justify="flex-end">
            {employee.country === Country.RU && !inactive && (
              <HeaderButton icon={edit} onClick={() => setIsEditModalVisible(true)} />
            )}

            {!inactive && !isCurrentUserOwner && <HeaderButton icon={deleteIcon} onClick={deleteButtonHandler} />}

            <HeaderButton icon={setting} onClick={settingButtonHandler} buttonType="transparent" />
          </Flex>
        </Flex>
      </Flex>

      <EditEmployeeSnilsModal
        employee={employee}
        open={isEditModalVisible}
        personalData={personalData}
        setOpen={setIsEditModalVisible}
        setPersonalData={setPersonalData}
      />

      <DeleteEmployeeModal
        employee={employee}
        open={isDeleteModalVisible}
        personalData={personalData}
        setOpen={setIsDeleteModalVisible}
      />
    </ItemBox>
  )
})
